// src/hooks/usePushNotifications.js
import { useState, useEffect, useCallback } from 'react';
import pushNotificationService from '../services/pushNotificationService';

/**
 * Custom hook for managing push notifications
 * 
 * Usage:
 * const {
 *   isSupported,
 *   isEnabled,
 *   permission,
 *   loading,
 *   error,
 *   enable,
 *   disable,
 *   sendTest,
 *   checkStatus
 * } = usePushNotifications();
 * 
 * @param {boolean} autoInit - Whether to automatically initialize on mount (default: true)
 * @returns {object} Notification state and control methods
 */
const usePushNotifications = (autoInit = true) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Check current notification status
   */
  const checkStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check browser support
      const supported = pushNotificationService.isSupported();
      setIsSupported(supported);

      if (!supported) {
        setLoading(false);
        return {
          supported: false,
          enabled: false,
          permission: 'unsupported'
        };
      }

      // Initialize service
      await pushNotificationService.initialize();

      // Get permission status
      const currentPermission = pushNotificationService.getPermissionStatus();
      setPermission(currentPermission);

      // Check if currently subscribed
      const subscribed = await pushNotificationService.isSubscribed();
      setIsEnabled(subscribed);

      return {
        supported,
        enabled: subscribed,
        permission: currentPermission
      };

    } catch (err) {
      console.error('Error checking notification status:', err);
      setError(err.message);
      return {
        supported: false,
        enabled: false,
        permission: 'error',
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Enable push notifications
   */
  const enable = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Subscribe to push notifications
      await pushNotificationService.subscribe();

      // Update state
      setIsEnabled(true);
      setPermission('granted');

      return {
        success: true,
        enabled: true,
        permission: 'granted'
      };

    } catch (err) {
      console.error('Error enabling notifications:', err);
      setError(err.message);
      
      // Update permission state if it changed
      const currentPermission = pushNotificationService.getPermissionStatus();
      setPermission(currentPermission);

      return {
        success: false,
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Disable push notifications
   */
  const disable = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Unsubscribe from push notifications
      await pushNotificationService.unsubscribe();

      // Update state
      setIsEnabled(false);

      return {
        success: true,
        enabled: false
      };

    } catch (err) {
      console.error('Error disabling notifications:', err);
      setError(err.message);
      
      return {
        success: false,
        error: err.message
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Send test notification
   */
  const sendTest = useCallback(async () => {
    try {
      setError(null);

      await pushNotificationService.sendTestNotification();

      return {
        success: true,
        message: 'Test notification sent'
      };

    } catch (err) {
      console.error('Error sending test notification:', err);
      setError(err.message);
      
      return {
        success: false,
        error: err.message
      };
    }
  }, []);

  /**
   * Request notification permission
   */
  const requestPermission = useCallback(async () => {
    try {
      setError(null);

      const result = await pushNotificationService.requestPermission();
      setPermission(result);

      return {
        success: result === 'granted',
        permission: result
      };

    } catch (err) {
      console.error('Error requesting permission:', err);
      setError(err.message);
      
      return {
        success: false,
        error: err.message
      };
    }
  }, []);

  /**
   * Get current subscription details
   */
  const getSubscription = useCallback(async () => {
    try {
      const subscription = await pushNotificationService.getSubscription();
      return {
        success: true,
        subscription
      };
    } catch (err) {
      console.error('Error getting subscription:', err);
      return {
        success: false,
        error: err.message
      };
    }
  }, []);

  /**
   * Show a local notification (for testing)
   */
  const showLocal = useCallback(async (title, options = {}) => {
    try {
      setError(null);

      await pushNotificationService.showLocalNotification(title, options);

      return {
        success: true,
        message: 'Local notification shown'
      };

    } catch (err) {
      console.error('Error showing local notification:', err);
      setError(err.message);
      
      return {
        success: false,
        error: err.message
      };
    }
  }, []);

  // Auto-initialize on mount if enabled
  useEffect(() => {
    if (autoInit) {
      checkStatus();
    }
  }, [autoInit, checkStatus]);

  return {
    // State
    isSupported,
    isEnabled,
    permission,
    loading,
    error,
    
    // Methods
    enable,
    disable,
    sendTest,
    checkStatus,
    requestPermission,
    getSubscription,
    showLocal,
    
    // Computed
    canEnable: isSupported && permission !== 'denied',
    needsPermission: permission === 'default'
  };
};

export default usePushNotifications;
